import React, { useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setFormType } from "../../redux/actions";
import { useNavigate, useLocation } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import axios from "axios";
import Footer from "../../components/footer/Footer";
import Navbar from "../../components/navbar/Navbar";
import useIsInIframe from "../../hooks/useIsInIframe";
import { saveStepdata } from "../../services/formService";
import { appointmentApiEndpoints } from "../../constants";
import { preserveAllUrlParams, restoreDynamicParamsFromStorage, extractUrlParameters } from "../../utils/urlParams";
import "./MeetingInfo.css";
import "../../components/form/Loader.css";
import { FiUser, FiMail, FiPhone, FiAlertCircle } from "react-icons/fi";
import { formatPhoneNumber } from "../../utils/phoneFormatter";

const defaultTimeSlots = {
  Morning: ["10:00", "10:15", "10:30", "10:45", "11:00", "11:15", "11:30", "11:45"],
  Noon: ["12:00", "12:15", "12:30", "12:45", "15:00", "15:15", "15:30", "15:45"],
  Evening: ["16:00", "16:15", "16:30", "16:45", "17:00"],
};

const localDateFromYMD = (ymd) => {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatDateLabel = (dateString) => {
  if (!dateString) return "";
  const d = localDateFromYMD(dateString);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const normalizeTimeToHHMM = (time) => {
  if (!time) return "";
  const parts = String(time).trim().split(":");
  const h = (parts[0] || "0").padStart(2, "0");
  const m = (parts[1] || "0").padStart(2, "0");
  return `${h}:${m}`;
};

const formatTimeLabel = (time) => normalizeTimeToHHMM(time);

const isTimeInPast = (selectedDate, time) => {
  if (!selectedDate || !time) return false;

  const now = new Date();
  const [h, m] = time.split(":").map(Number);
  const slot = new Date();
  slot.setHours(h, m, 0, 0);

  const isToday =
    selectedDate.getFullYear() === now.getFullYear() &&
    selectedDate.getMonth() === now.getMonth() &&
    selectedDate.getDate() === now.getDate();

  return isToday && slot < now;
};

const validatePhoneNumber = (phoneNumber) => {
  // Accepts formats: (XXX) XXX-XXXX, +1 (XXX) XXX-XXXX, XXX-XXX-XXXX, etc.
  const re = /^(?:\+1[.\-\s]?)?(?:\(?\d{3}\)?[.\-\s]?)\d{3}[.\-\s]?\d{4}$/;
  return re.test(String(phoneNumber).trim());
};

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  return re.test(String(email).toLowerCase());
};

const validateWithRegex = (value, regex) => {
  return regex.test(String(value).trim()) && value.trim().length > 0;
};

const nameRegex = /^[a-zA-Z\s'-]+$/;

const MeetingInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const formType = useSelector((state) => state.formType);
  const formData = useSelector((state) => state.formData);
  const currentStep = useSelector((state) => state.currentStep);
  const sessionId = useSelector((state) => state.sessionId);
  const applicationId = useSelector((state) => state.applicationId);
  const isAppointmentType = formType === "appointment";
  const isInIframe = useIsInIframe();
  const searchParams = new URLSearchParams(location.search);
  const lar = searchParams.get("lar") || localStorage.getItem("lar") || "admin";
  const utm_source =
    searchParams.get("utm_source") ||
    localStorage.getItem("utm_source") ||
    "direct";
  const utm_term =
    searchParams.get("utm_term") ||
    localStorage.getItem("utm_term");
  const utm_campaign =
    searchParams.get("utm_campaign") ||
    localStorage.getItem("utm_campaign");
  const utm_type =
    searchParams.get("utm_type") ||
    localStorage.getItem("utm_type");

  const homeUrl = useMemo(() => {
    // Preserve all URL parameters (both registered and dynamic)
    const sp = preserveAllUrlParams(location.search);
    
    // Restore dynamic parameters from localStorage if not in URL
    restoreDynamicParamsFromStorage(sp);
    
    // Ensure required parameters have defaults if missing
    if (!sp.has("lar")) {
      sp.set("lar", lar);
    }
    if (!sp.has("utm_source")) {
      sp.set("utm_source", utm_source);
    }
    
    // Handle optional parameters from localStorage if not in URL
    if (!sp.has("utm_term") && utm_term) {
      sp.set("utm_term", utm_term);
    }
    if (!sp.has("utm_campaign") && utm_campaign) {
      sp.set("utm_campaign", utm_campaign);
    }
    if (!sp.has("utm_type") && utm_type) {
      sp.set("utm_type", utm_type);
    }
    
    return `${process.env.REACT_APP_BASE_URL}?${sp.toString()}`;
  }, [location.search, lar, utm_source, utm_term, utm_campaign, utm_type]);

  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("language");
    return saved === "Spanish" ? "Spanish" : "English";
  });

  const [timeSlots] = useState(defaultTimeSlots);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [slotsError, setSlotsError] = useState(false);
  const [bookedSlots, setBookedSlots] = useState({});
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState(null);
  const [emailError, setEmailError] = useState(null);

  const [selectedDate, setSelectedDate] = useState(null);
  const [meetingDate, setMeetingDate] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [customMeetingTime, setCustomMeetingTime] = useState("");
  const [bestTimeToMeet, setBestTimeToMeet] = useState("");
  const [isBooking, setIsBooking] = useState(false);
  const [showError, setShowError] = useState(false);

  // Set formType to "appointment" only if it's not already set
  // This preserves the home page flow where formType is set before navigation
  useEffect(() => {
    if (!formType) {
      dispatch(setFormType("appointment"));
    }
  }, [dispatch, formType]);

  // Add class to body and html to allow scrolling with hidden scrollbar in iframe mode
  useEffect(() => {
    if (isInIframe) {
      document.body.classList.add('meetinginfo-page');
      document.documentElement.classList.add('meetinginfo-page');
    }
    
    return () => {
      document.body.classList.remove('meetinginfo-page');
      document.documentElement.classList.remove('meetinginfo-page');
    };
  }, [isInIframe]);

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "language") {
        setLang(e.newValue === "Spanish" ? "Spanish" : "English");
      }
    };

    const handleLangChanged = (e) => {
      const next = e?.detail;
      setLang(next === "Spanish" ? "Spanish" : "English");
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("langChanged", handleLangChanged);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("langChanged", handleLangChanged);
    };
  }, []);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);

    const larFromUrl = sp.get("lar");
    const utmSourceFromUrl = sp.get("utm_source");
    const utmTermFromUrl = sp.get("utm_term");
    const utmCampaignFromUrl = sp.get("utm_campaign");
    const utmTypeFromUrl = sp.get("utm_type");

    if (larFromUrl) {
      localStorage.setItem("lar", larFromUrl);
    }

    if (utmSourceFromUrl) {
      localStorage.setItem("utm_source", utmSourceFromUrl);
    }

    if (utmTermFromUrl) {
      localStorage.setItem("utm_term", utmTermFromUrl);
    } else {
      localStorage.removeItem("utm_term");
    }

    if (utmCampaignFromUrl) {
      localStorage.setItem("utm_campaign", utmCampaignFromUrl);
    } else {
      localStorage.removeItem("utm_campaign");
    }
    
    if (utmTypeFromUrl) {
      localStorage.setItem("utm_type", utmTypeFromUrl);
    } else {
      localStorage.removeItem("utm_type");
    }
    
    // Store dynamic parameters in localStorage
    const { dynamicParameters } = extractUrlParameters(sp);
    if (dynamicParameters && dynamicParameters.length > 0) {
      localStorage.setItem("dynamic_parameters", JSON.stringify(dynamicParameters));
    } else {
      localStorage.removeItem("dynamic_parameters");
    }
  }, [location.search]);

  const t = useMemo(() => {
    const translations = {
      English: {
        scheduleAppointment: "Schedule Appointment",
        subtitleAppointment: "Share your details and choose a time that works for you.",
        subtitleCall: "Choose a preferred date and time for a quick call.",
        yourDetails: "Your Details",
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email",
        phoneNumber: "Phone Number",
        pickMeetingTime: "Pick a Meeting Time",
        loadingSlots: "Loading available slots...",
        failedToLoadSlots: "Failed to load available time slots",
        checkConnection: "Please check your connection and try again",
        retry: "Retry",
        date: "Date",
        time: "Time",
        allSlotsBooked: "All slots between 8AM - 5PM are booked. Please try another date.",
        selected: "Selected:",
        booking: "Booking...",
        bookAppointment: "Book Appointment",
        back: "Back",
        skip: "Skip",
        bookingYourAppointment: "Booking your appointment",
        pleaseWait: "Please wait...",
        bookingFailed: "Appointment booking failed",
        tryAgain: "Try Again",
        ok: "OK",
        periodMorning: "Morning",
        periodNoon: "Noon",
        periodEvening: "Evening",
      },
      Spanish: {
        scheduleAppointment: "Programar Cita",
        subtitleAppointment: "Comparta sus datos y elija un horario que le convenga.",
        subtitleCall: "Elija una fecha y hora preferidas para una llamada rápida.",
        yourDetails: "Sus datos",
        firstName: "Nombre",
        lastName: "Apellido",
        email: "Correo electrónico",
        phoneNumber: "Número de teléfono",
        pickMeetingTime: "Elija la hora de la reunión",
        loadingSlots: "Cargando horarios disponibles...",
        failedToLoadSlots: "No se pudieron cargar los horarios disponibles",
        checkConnection: "Verifique su conexión e inténtelo de nuevo",
        retry: "Reintentar",
        date: "Fecha",
        time: "Hora",
        allSlotsBooked: "Todos los horarios entre 8 a. m. y 5 p. m. están reservados. Intente con otra fecha.",
        selected: "Seleccionado:",
        booking: "Reservando...",
        bookAppointment: "Reservar cita",
        back: "Atrás",
        skip: "Omitir",
        bookingYourAppointment: "Reservando su cita",
        pleaseWait: "Por favor espere...",
        bookingFailed: "La reserva de la cita falló",
        tryAgain: "Intentar de nuevo",
        ok: "OK",
        periodMorning: "Mañana",
        periodNoon: "Mediodía",
        periodEvening: "Tarde",
      },
    };

    return translations[lang] || translations.English;
  }, [lang]);

  const locale = lang === "Spanish" ? "es-ES" : "en-US";

  const formatDateLabelByLang = (dateString) => {
    if (!dateString) return "";
    const d = localDateFromYMD(dateString);
    return d.toLocaleDateString(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const periodLabel = (periodKey) => {
    if (periodKey === "Morning") return t.periodMorning;
    if (periodKey === "Noon") return t.periodNoon;
    if (periodKey === "Evening") return t.periodEvening;
    return periodKey;
  };

  useEffect(() => {
    const loadSlots = async () => {
      try {
        setSlotsError(false);
        const res = await axios.get(appointmentApiEndpoints.GET_APPOINTMENTS, {
          params: { lar },
        });

        const arr = res.data?.data || [];
        const map = {};

        arr.forEach((appt) => {
          const dateKey = appt.AppointmentDate;
          const timeKey = normalizeTimeToHHMM(appt.AppointmentTime);
          if (!map[dateKey]) map[dateKey] = [];
          if (!map[dateKey].includes(timeKey)) {
            map[dateKey].push(timeKey);
          }
        });

        setBookedSlots(map);
      } catch (err) {
        console.error("GetAppointments error:", err);
        setSlotsError(true);
      } finally {
        setLoadingSlots(false);
      }
    };
    loadSlots();
  }, [lar]);

  useEffect(() => {
    if (!isAppointmentType) {
      try {
        const saved = JSON.parse(localStorage.getItem("formData"));
        if (saved) {
          if (saved.firstName) setFirstName(saved.firstName);
          if (saved.lastName) setLastName(saved.lastName);

          if (!saved.firstName && saved.fullName) {
            const parts = String(saved.fullName).trim().split(" ");
            setFirstName(parts[0] || "");
            setLastName(parts.slice(1).join(" ") || "");
          }

          setEmail(saved.email || "");
          setPhone(saved.phoneNumber || "");
        }
      } catch {
        // ignore parse errors
      }
    }
  }, [isAppointmentType]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, "0");
    const d = `${date.getDate()}`.padStart(2, "0");
    setMeetingDate(`${y}-${m}-${d}`);
    setSelectedPeriod("");
    setSelectedTime("");
    setBestTimeToMeet("");
    setCustomMeetingTime("");
  };

  const handleSlotClick = (period, time) => {
    setSelectedPeriod(period);
    setSelectedTime(time);
    setBestTimeToMeet(period);
    setCustomMeetingTime(normalizeTimeToHHMM(time));
  };

  const canBook = isAppointmentType
    ? firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      email.length > 0 &&
      validateEmail(email) &&
      !emailError &&
      validatePhoneNumber(phone) &&
      !phoneError &&
      meetingDate &&
      customMeetingTime &&
      !slotsError
    : meetingDate && customMeetingTime && !slotsError;

  const isAllSlotsUnavailable = () => {
    if (!meetingDate || !selectedDate) return false;

    const allTimes = Object.values(timeSlots).flat();
    const booked = bookedSlots[meetingDate] || [];

    const allBooked = allTimes.every((t) =>
      booked.includes(normalizeTimeToHHMM(t)) ||
      isTimeInPast(selectedDate, t)
    );
    const now = new Date();

    const isToday =
      selectedDate.getDate() === now.getDate() &&
      selectedDate.getMonth() === now.getMonth() &&
      selectedDate.getFullYear() === now.getFullYear();

    return allBooked || (isToday && now.getHours() >= 17);
  };

  const finalizeSubmit = async () => {
    try {
      setIsBooking(true);
      setShowError(false);

      const fullName = `${firstName} ${lastName}`.trim();

      // const finalStepData = {
      //   session_id: sessionId,
      //   application_id: applicationId || null,
      //   current_step: currentStep + 2,
      //   status: "submitted",
      //   form_data: {
      //     ...formData,
      //     firstName,
      //     lastName,
      //     fullName,
      //     email,
      //     phone,
      //     meetingDate,
      //     bestTimeToMeet,
      //     customMeetingTime,
      //   },
      // };

      // await saveStepdata(finalStepData);

      const Phone = String(phone || "").replace(/\D/g, "");

      const bookingBody = {
        session_id: sessionId,
        application_id: applicationId || "direct",
        lar,
        utm_source,
        userName: fullName,
        userEmail: email,
        userPhone: Phone,
        date: meetingDate,
        time: customMeetingTime,
        language: lang,
      };

      if (utm_term) {
        bookingBody.utm_term = utm_term;
      }

      const res = await axios.post(
        appointmentApiEndpoints.BOOK_APPOINTMENT,
        bookingBody
      );

      if (res) {
        navigate(process.env.REACT_APP_THANKYOU_URL);
      }
    } catch (err) {
      console.error("Book appointment error:", err);

      setIsBooking(false);
      if (err.response) {
        console.log("Error response:", err.response.status);
        setShowError(true);
      } else if (err.request) {
        console.log("No response from server");
        setShowError(true);
      } else {
        console.log("Unknown error");
        setShowError(true);
      }
    }
  };

  const retryLoadSlots = async () => {
    setLoadingSlots(true);
    setSlotsError(false);
    try {
      const res = await axios.get(appointmentApiEndpoints.GET_APPOINTMENTS, {
        params: { lar },
      });

      const arr = res.data?.data || [];
      const map = {};

      arr.forEach((appt) => {
        const dateKey = appt.AppointmentDate;
        const timeKey = normalizeTimeToHHMM(appt.AppointmentTime);
        if (!map[dateKey]) map[dateKey] = [];
        if (!map[dateKey].includes(timeKey)) {
          map[dateKey].push(timeKey);
        }
      });

      setBookedSlots(map);
      setSlotsError(false);
    } catch (err) {
      console.error("GetAppointments error:", err);
      setSlotsError(true);
    } finally {
      setLoadingSlots(false);
    }
  };

  return (
    <div className="meetinginfo-wrapper">
      {!isInIframe && <Navbar />}
      <div className="form-containers">
        <div className="meeting-layout meetinginfo-layout">
          <div className="question-col">
            <div className="main-questions">{t.scheduleAppointment}</div>
            <p className="meetinginfo-subtitle">
              {isAppointmentType
                ? t.subtitleAppointment
                : t.subtitleCall}
            </p>
          </div>
          <div className="options-col">
            {isAppointmentType && (
              <div className="option-box meetinginfo-card">
                <h2 className="meetinginfo-title">{t.yourDetails}</h2>

                <div className="appointment-contact-form">
                  <label className="field-label">
                    <FiUser /> {t.firstName}
                  </label>
                  <input
                    className="text-input"
                    value={firstName}
                    maxLength={75}
                    onChange={(e) => {
                      const clean = e.target.value.replace(/[^a-zA-Z\s'-]/g, "");
                      setFirstName(clean);
                    }}
                  />

                  <label className="field-label">
                    <FiUser /> {t.lastName}
                  </label>
                  <input
                    className="text-input"
                    value={lastName}
                    maxLength={75}
                    onChange={(e) => {
                      const clean = e.target.value.replace(/[^a-zA-Z\s'-]/g, "");
                      setLastName(clean);
                    }}
                  />

                  <label className="field-label">
                    <FiMail /> {t.email}
                  </label>
                  <input
                    className="text-input"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      const clean = e.target.value.replace(/[^a-zA-Z0-9@._-]/g, "");
                      setEmail(clean);
                      if (!String(clean || "").trim()) {
                        setEmailError("Email is required");
                      } else if (!validateEmail(clean)) {
                        setEmailError("Invalid email address");
                      } else {
                        setEmailError(null);
                      }
                    }}
                  />
                  {emailError && (
                    <p className="field-error-message">
                      {emailError}
                    </p>
                  )}
                  <label className="field-label">
                    <FiPhone /> {t.phoneNumber}
                  </label>
                  <input
                    className="text-input"
                    value={phone}
                    maxLength={14}
                    onChange={(e) => {
                      const next = e.target.value;
                      // Format phone number as user types
                      const formatted = formatPhoneNumber(next);
                      setPhone(formatted);
                      if (!String(formatted || "").trim()) {
                        setPhoneError("Phone number is required");
                      } else if (!validatePhoneNumber(formatted)) {
                        setPhoneError("Invalid phone number");
                      } else {
                        setPhoneError(null);
                      }
                    }}
                  />
                  {phoneError && (
                    <p style={{ color: "red", fontSize: "0.8rem", marginTop: 4 }}>
                      {phoneError}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="option-box meetinginfo-card">
              <h2 className="meetinginfo-title">{t.pickMeetingTime}</h2>

              {loadingSlots ? (
                <div className="slots-loading-container">
                  <div className="slots-spinner" />
                  <p className="slots-loading-text">{t.loadingSlots}</p>
                </div>
              ) : slotsError ? (
                <div className="slots-error-container">
                  <FiAlertCircle className="slots-error-icon" />
                  <p className="slots-error-text">{t.failedToLoadSlots}</p>
                  <p className="slots-error-subtext">{t.checkConnection}</p>
                  <button className="slots-retry-button" onClick={retryLoadSlots}>
                    {t.retry}
                  </button>
                </div>
              ) : (
                <>
                  <div className="meetinginfo-section">
                    <span className="sub-questions">{t.date}</span>
                    <Calendar
                      onChange={handleDateChange}
                      minDate={new Date()}
                      value={selectedDate}
                    />
                  </div>

                  {selectedDate && (
                    <>
                      <span className="sub-questions">{t.time}</span>

                      {isAllSlotsUnavailable() && (
                        <p style={{ color: "red", fontSize: "0.8rem", marginTop: 4 }}>
                          {t.allSlotsBooked}
                        </p>
                      )}

                      <div className="time-period-row">
                        {Object.keys(timeSlots).map((period) => {
                          const periodSlots = timeSlots[period];
                          const bookedForDay = bookedSlots[meetingDate] || [];

                          const isPeriodFullyBooked = periodSlots.every(
                            (t) =>
                              bookedForDay.includes(normalizeTimeToHHMM(t)) ||
                              isTimeInPast(selectedDate, t)
                          );

                          return (
                            <button
                              key={period}
                              className={
                                "time-period-btn" +
                                (selectedPeriod === period ? " selected" : "")
                              }
                              disabled={isPeriodFullyBooked}
                              onClick={() => {
                                if (!isPeriodFullyBooked) {
                                  setSelectedPeriod(period);
                                  setSelectedTime("");
                                }
                              }}
                            >
                              {periodLabel(period)}
                            </button>
                          );
                        })}
                      </div>

                      {selectedPeriod && (
                        <div className="time-slot-row">
                          {timeSlots[selectedPeriod].map((t) => {
                            const normalized = normalizeTimeToHHMM(t);
                            const bookedForDay = bookedSlots[meetingDate] || [];
                            const isBooked = bookedForDay.includes(normalized);
                            const isPast = isTimeInPast(selectedDate, t);

                            return (
                              <button
                                key={t}
                                disabled={isBooked || isPast}
                                className={
                                  "time-slot-btn" +
                                  (selectedTime === t ? " selected" : "") +
                                  (isBooked ? " disabled-slot" : "") +
                                  (isPast ? " past-slot" : "")
                                }
                                onClick={() =>
                                  !isBooked && !isPast && handleSlotClick(selectedPeriod, t)
                                }
                              >
                                <span className="time-slot-label">
                                  {formatTimeLabel(t)}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {meetingDate && selectedTime && (
                        <div className="selected-summary">
                          <span>{t.selected}</span>
                          <strong>
                            {formatDateLabelByLang(meetingDate)} ·{" "}
                            {formatTimeLabel(selectedTime)}
                          </strong>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            <div
              className={
                "meetinginfo-actions" + (!isAppointmentType ? " meetinginfo-actions-compact" : "")
              }
            >
              <button
                className={
                  "submit-buttons" + (!isAppointmentType ? " submit-buttons-compact" : "")
                }
                disabled={!canBook || isBooking}
                onClick={finalizeSubmit}
              >
                {isBooking ? t.booking : t.bookAppointment}
              </button>
              <button
                className={
                  "back-buttons" + (!isAppointmentType ? " back-buttons-compact" : "")
                }
                onClick={() => {
                  if (isBooking) return;
                  if (isAppointmentType) {
                    navigate(homeUrl);
                  } else {
                    navigate(process.env.REACT_APP_THANKYOU_URL);
                  }
                }}
                disabled={isBooking}
              >
                {isAppointmentType ? t.back : t.skip}
              </button>
            </div>
          </div>
        </div>
      </div>

      {!isInIframe && <Footer />}

      {/* Loader overlay */}
      {isBooking && (
        <div className="df-loader-overlay">
          <div className="df-loader-card">
            <div className="df-spinner" />
            <p className="df-loader-text">{t.bookingYourAppointment}</p>
            <p className="df-loader-subtext">{t.pleaseWait}</p>
          </div>
        </div>
      )}

      {/* Error popup */}
      {showError && (
        <div className="mi-error-overlay">
          <div className="mi-error-card">
            <h3 className="mi-error-title">{t.bookingFailed}</h3>
            <p className="mi-error-message">
              {t.tryAgain}
            </p>
            <button
              className="mi-error-button"
              onClick={() => setShowError(false)}
            >
              {t.ok}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingInfo;